import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from 'react-i18next';
import StudentModal from "../../modals/admin/StudentModal";
import IncidentReportingModal from "../../modals/admin/IncidentReportingModal";
import toast, { Toaster } from "react-hot-toast";
import {
  ChevronDown,
  ChevronUp,
  Pencil,
  Trash2,
  User,
  SendHorizonal,
  FilterX,
} from "lucide-react";
import StudentCards from "../../Components/admin/Cards/StudentCards";
import { useDispatch, useSelector } from "react-redux";
import CustomDropdown from "../../Components/admin/CustomDropdown";
import {
  updateStudent,
  deleteStudent,
  fetchStudents,
  addStudents,
  setPageNumber,
  setNameFilter,
  setCampusId,
  setClassId,
  setSectionId,
  resetFilters,
} from "../../redux/studentSlice";
import { fetchClassesV1 } from "../../redux/classSlice";
import { fetchFeeStructures } from "../../redux/feeStructureSlice";
import { Users, UserX, Trophy, Medal, AlertCircle, Plus } from "lucide-react";
import NotificationsModal from "../../Components/Common/NotificationModal";
import { formatDate, uploadImage } from "../../utils";
import { fetchCampusClassesDetail } from "../../redux/campusesSlice";
import {
  sendNotification,
  clearNotificationStatus,
} from "../../redux/notificationsSlice";
import {
  fetchIncidentReports,
  createIncidentReport,
  updateIncidentReport,
} from "../../redux/incidentReportSlice";

const Students = () => {
  const { t } = useTranslation();
  const { reset, register, watch, handleSubmit, setValue, getValues } =
    useForm();
  const password = watch("password");
  const dispatch = useDispatch();
  const students = useSelector((state) => state.students.students);
  const loading = useSelector((state) => state.students.loading);
  const nameFilter = useSelector((state) => state.students.nameFilter);
  const reduxCampusId = useSelector((state) => state.students.campusId);
  const reduxClassId = useSelector((state) => state.students.classId);
  const reduxSectionId = useSelector((state) => state.students.sectionId);
  const [currentPicture, setCurrentPicture] = useState(null);
  const [selectedClass, setSelectedClass] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isNotificationsModal, setIsNotificationsModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [uploadType, setUploadType] = useState("");
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [currentStudentId, setCurrentStudentId] = useState(null);
  const [expandedRowIndex, setExpandedRowIndex] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [campusFilter, setCampusFilter] = useState("");
  const [standardFilter, setStandardFilter] = useState("");
  const [sectionFilter, setSectionFilter] = useState("");
  const [tileFilter, setTileFilter] = useState("");
  const [selectedCard, setSelectedCard] = useState(null);
  const [searchTimeout, setSearchTimeout] = useState(null);

  const totalCount = useSelector((state) => state.students.totalCount);
  const pageSize = useSelector((state) => state.students.pageSize);
  const pageNumber = useSelector((state) => state.students.pageNumber);

  const classes = useSelector((state) => state?.classes?.classes || []);
  const [sections, setSections] = useState([]);

  const { campusClassesDetail } = useSelector((state) => state?.campuses || []);

  const { feeStructures } = useSelector((state) => state.feeStructures);

  const { incidentReports } = useSelector((state) => state.incidentReports);

  const notificationStatus = useSelector(
    (state) => state.notifications.sendStatus
  );
  const notificationError = useSelector((state) => state.notifications.error);

  const [selectedIncident, setSelectedIncident] = useState(null);
  const [isIncidentModalOpen, setIsIncidentModalOpen] = useState(false);
  const [isIncidentReportModalOpen, setIsIncidentReportModalOpen] = useState(false);
  const [selectedStudentForIncident, setSelectedStudentForIncident] = useState(null);
  const [incidentToEdit, setIncidentToEdit] = useState(null);

  useEffect(() => {
    dispatch(fetchFeeStructures());
    dispatch(fetchCampusClassesDetail());
    dispatch(fetchIncidentReports());
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      fetchStudents({
        pageNumber,
        pageSize,
        nameFilter: nameFilter,
        campusId: reduxCampusId,
        classId: reduxClassId,
        sectionId: reduxSectionId,
      })
    );
  }, [
    dispatch,
    pageNumber,
    pageSize,
    nameFilter,
    reduxCampusId,
    reduxClassId,
    reduxSectionId,
  ]);

  useEffect(() => {
    dispatch(fetchClassesV1({ pageNumber: 1, pageSize: 100 }));
  }, [dispatch]);

  useEffect(() => {
    if (reduxCampusId) {
      const selectedCampus = campusClassesDetail?.find(
        (campus) => campus.id === reduxCampusId
      );
      setCampusFilter(selectedCampus ? selectedCampus.name : "");
    } else {
      setCampusFilter("");
    }

    let selectedClass = null;
    if (reduxClassId) {
      campusClassesDetail?.forEach((campus) => {
        const foundClass = campus.classes.find((cls) => cls.id === reduxClassId);
        if (foundClass) selectedClass = foundClass;
      });
      setStandardFilter(selectedClass ? selectedClass.className : "");
    } else {
      setStandardFilter("");
    }

    let sectionName = "";
    if (reduxClassId && reduxSectionId) {
      campusClassesDetail?.forEach((campus) => {
        const selectedClass = campus.classes.find((cls) => cls.id === reduxClassId);
        if (selectedClass) {
          const selectedSection = selectedClass.sections.find(
            (section) => section.id === reduxSectionId
          );
          if (selectedSection) sectionName = selectedSection.sectionName;
        }
      });
      setSectionFilter(sectionName);
    } else {
      setSectionFilter("");
    }
  }, [reduxCampusId, reduxClassId, reduxSectionId, campusClassesDetail]);

  useEffect(() => {
    if (notificationStatus === "succeeded") {
      toast.success(t("NotificationSentSuccess"));
      dispatch(clearNotificationStatus());
    } else if (notificationStatus === "failed" && notificationError) {
      toast.error(`${t("NotificationSendFailed")}: ${notificationError}`);
      dispatch(clearNotificationStatus());
    }
  }, [notificationStatus, notificationError, dispatch, t]);

  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  const openIncidentReportModal = (student, incident = null) => {
    setSelectedStudentForIncident({
      id: student.student?.id,
      name: `${student.student?.firstName} ${student.student?.lastName}`,
    });
    setIncidentToEdit(incident);
    setIsIncidentReportModalOpen(true);
  };

  const handleIncidentSubmit = async (incidentData) => {
    const formData = new FormData();
    Object.entries(incidentData).forEach(([key, value]) => {
      if (key === "attachments" && value[0]) {
        formData.append("attachments", value[0]);
      } else if (value) {
        formData.append(key, value);
      }
    });
  
    try {
      if (incidentToEdit) {
        await dispatch(updateIncidentReport({ id: incidentToEdit.id, incidentData: formData })).unwrap();
      } else {
        await dispatch(createIncidentReport(formData)).unwrap();
      }
      await dispatch(fetchIncidentReports()).unwrap();
      setIsIncidentReportModalOpen(false);
      setIncidentToEdit(null);
    } catch (error) {
      console.error("Failed to submit incident:", error);
    }
  };

  const topPerformers = ["A", "A+"];
  const studentcardData = [
    {
      title: t("TotalStudents"),
      icon: Users,
      number: totalCount || 0,
      status: "",
    },
    {
      title: t("TopPerformers"),
      icon: Trophy,
      number:
        students?.filter((s) =>
          topPerformers.includes(s?.academicDetails?.overallAnnualResult)
        )?.length || 0,
      status: "TP",
    },
    {
      title: t("ExcellentAttendance"),
      icon: Medal,
      number:
        students?.filter(
          (s) => parseFloat(s?.academicDetails?.attendance) >= 90
        )?.length || 0,
      status: "EA",
    },
    {
      title: t("FeeDefaulters"),
      icon: UserX,
      number: 0,
      status: "FD",
    },
  ];

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    if (value.length === 0 || value.length >= 3) {
      setSearchTimeout(setTimeout(() => {
        dispatch(setNameFilter(value));
      }, 500));
    }
  };

  const handleCampusChange = (value) => {
    setCampusFilter(value);

    const selectedCampus = campusClassesDetail?.find((campus) => campus.name === value);
    const campusId = selectedCampus ? selectedCampus.id : "";

    dispatch(setCampusId(campusId));
    dispatch(setClassId(""));
    dispatch(setSectionId(""));
    setStandardFilter("");
    setSectionFilter("");
  };

  const handleClassChange = (value) => {
    setStandardFilter(value);
    let classId = "";

    campusClassesDetail?.forEach((campus) => {
      const selectedClass = campus.classes.find((cls) => cls.className === value);
      if (selectedClass) classId = selectedClass.id;
    });

    dispatch(setClassId(classId));
    dispatch(setSectionId(""));
    setSectionFilter("");
  };

  const handleSectionChange = (value) => {
    setSectionFilter(value);
    let sectionId = "";

    if (reduxClassId) {
      campusClassesDetail?.forEach((campus) => {
        const selectedClass = campus.classes.find((cls) => cls.id === reduxClassId);
        if (selectedClass) {
          const section = selectedClass.sections.find(
            (section) => section.sectionName === value
          );
          sectionId = section ? section.id : "";
        }
      });
    }

    dispatch(setSectionId(sectionId));
  };

  const handleClearFilters = () => {
    dispatch(resetFilters());
    setSearchQuery("");
    setCampusFilter("");
    setStandardFilter("");
    setSectionFilter("");
    setTileFilter("");
    setSelectedCard(null);
  };

  const onSubmit = async (data, selectedFile) => {
    let fileUrl = null;
    if (selectedFile) {
      fileUrl = await uploadImage(selectedFile);
    } else if (isEdit) {
      fileUrl = currentPicture;
    }
    const transformedPayload = {
      firstName: data.firstName,
      lastName: data.lastName,
      phone: data.phone,
      cnic: data.cnic,
      location: data.location,
      dob: data.dob,
      email: data.email,
      password: data.password,
      gender: data.gender,
      admissionDate: data.admissionDate,
      maritalStatus: data.maritalStatus,
      sectionId: data.sectionId,
      campusId: data.campusId,
      userStatus: data.userStatus,
      feeSetupId: data.feeSetupId,
      discount: data.discount || [],
      dokLevel: data.dokLevel || "",
      ibAttributes: data.ibAttributes ? [data.ibAttributes] : [],

      userDetails: {
        fatherName: data.userDetails?.fatherName || "",
        fatherCNIC: data.userDetails?.fatherCNIC || "",
        fatherContact: data.userDetails?.fatherContact || "",
        fatherProfession: data.userDetails?.fatherProfession || "",
        motherName: data.userDetails?.motherName || "",
        motherCNIC: data.userDetails?.motherCNIC || "",
        motherContact: data.userDetails?.motherContact || "",
        motherProfession: data.userDetails?.motherProfession || "",
        parentEmail: data.userDetails?.parentEmail || "",
      },
      feeAndDues: {
        monthlyFee: data.feeAndDues?.monthlyFee || 0,
        lmsFee: data.feeAndDues?.lmsFee || 0,
        extraCurricularActFee: data.feeAndDues?.extraCurricularActFee || 0,
        anySpecialDiscount: data.feeAndDues?.anySpecialDiscount || 0,
        siblingDiscount: data.feeAndDues?.siblingDiscount || 0,
        employeeKidDiscount: data.feeAndDues?.employeeKidDiscount || 0,
        userStatus: data.userDetails?.userStatus || 0,
        discountName: data.userDetails?.discountName || 0,
        scholarshipName: data.userDetails?.scholarshipName || 0,
      },
      academicDetails: {
        firstSemResult: data.academicDetails?.firstSemResult || 0,
        secondSemResult: data.academicDetails?.secondSemResult || 0,
        thirdSemResult: data.academicDetails?.thirdSemResult || 0,
        overallAnnualResult: data.academicDetails?.overallAnnualResult || 0,
      },
    };

    if (fileUrl) {
      transformedPayload["profilePicture"] = fileUrl || "";
    }

    if (isEdit) {
      dispatch(
        updateStudent({
          id: currentStudentId,
          updatedStudent: transformedPayload,
        })
      );
    } else {
      dispatch(addStudents(transformedPayload));
    }

    reset();
    setIsModalOpen(false);
    handleModalClose();
  };

  const handleEdit = (student) => {
    if (student) {
      setValue("firstName", student.student?.firstName);
      setValue("lastName", student.student?.lastName);
      setValue("email", student.student?.email);
      setValue("phone", student.student?.phone);
      setValue("location", student.student?.location);
      setValue("dob", formatDate(student.student?.dob));
      setValue("gender", student.student?.gender);
      setValue("password", "");
      setValue("admissionDate", formatDate(student.student?.admissionDate));
      setValue("cnic", student.student?.cnic);
      setValue("userStatus", student.student?.userStatus);
      setValue("isActive", student.student?.isActive);
      setValue("enrollmentNo", student.student?.enrollmentNo);
      setValue("maritalStatus", student.student?.maritalStatus);
      setValue("profilePicture", student.student?.profilePicture);

      setValue("userDetails.fatherName", student.userDetails?.fatherName);
      setValue("userDetails.fatherCNIC", student.userDetails?.fatherCNIC);
      setValue("userDetails.fatherContact", student.userDetails?.fatherContact);
      setValue(
        "userDetails.fatherProfession",
        student.userDetails?.fatherProfession
      );
      setValue("userDetails.motherName", student.userDetails?.motherName);
      setValue("userDetails.motherCNIC", student.userDetails?.motherCNIC);
      setValue("userDetails.motherContact", student.userDetails?.motherContact);
      setValue(
        "userDetails.motherProfession",
        student.userDetails?.motherProfession
      );
      setValue("userDetails.parentEmail", student.userDetails?.parentEmail);

      setValue(
        "academicDetails.courseLevel",
        student.academicDetails?.courseLevel
      );
      setValue("campusId", student?.student.campusId);
      const classId = student.assignedClasses?.[0]?.classId || "";
      const sectionId =
        student.assignedClasses?.[0]?.classSections?.[0]?.id || "";

      setValue("classId", classId);
      setValue("sectionId", sectionId);
      setValue("campusId", student?.student.campusId);
      setValue("feeSetupId", student?.student.feeSetupId);
      const discountValue = student?.student.discount
        ? Array.isArray(student.student.discount)
          ? student.student.discount
          : [student.student.discount]
        : [];
      setValue("discount", discountValue);
      setValue("dokLevel", student?.student.dokLevel);
      setValue("ibAttributes", student?.student.ibAttributes?.[0] || "");

      setValue(
        "academicDetails.firstSemResult",
        student.academicDetails?.firstSemResult
      );
      setValue(
        "academicDetails.secondSemResult",
        student.academicDetails?.secondSemResult
      );
      setValue(
        "academicDetails.thirdSemResult",
        student.academicDetails?.thirdSemResult
      );
      setValue(
        "academicDetails.overallAnnualResult",
        student.academicDetails?.overallAnnualResult
      );

      setValue("feeAndDues.monthlyFee", student.feeAndDues?.monthlyFee);
      setValue(
        "feeAndDues.anySpecialDiscount",
        student.feeAndDues?.anySpecialDiscount
      );
      setValue(
        "feeAndDues.siblingDiscount",
        student.feeAndDues?.siblingDiscount
      );
      setValue(
        "feeAndDues.employeeKidDiscount",
        student.feeAndDues?.employeeKidDiscount
      );
      setValue("feeAndDues.lmsFee", student.feeAndDues?.lmsFee);
      setValue(
        "feeAndDues.extraCurricularActFee",
        student.feeAndDues?.extraCurricularActFee
      );

      setValue("attendance", student.attendance);
      setValue("teachers_observation", student.teachers_observation);

      setSelectedClass(classId);
      let sections = [];
      campusClassesDetail?.forEach((campus) => {
        const selectedClass = campus.classes.find((cls) => cls.id === classId);
        if (selectedClass) {
          sections = selectedClass.sections.map((section) => ({
            id: section.id,
            name: section.sectionName,
          }));
        }
      });
      setSections(sections);
      setCurrentStudentId(student.student?.id);
      setCurrentPicture(student.student?.profilePicture);
      setIsEdit(true);
      setIsModalOpen(true);
    }
  };

  const handleModalClose = () => {
    setUploadType("");
    setIsEdit(false);
  };

  const handleUploadTypeChange = (type) => {
    setUploadType(type);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await dispatch(deleteStudent(id)).unwrap();
      toast.success(t("StudentDeletedSuccess"));
    } catch (error) {
      toast.error(t("StudentDeleteFailed"));
    }
  };

  const handleStudentCardClick = (card, index) => {
    setTileFilter(card.status);
    setSelectedCard(index);

    dispatch(setNameFilter(""));
    dispatch(setCampusId(""));
    dispatch(setClassId(""));
    dispatch(setSectionId(""));
    setSearchQuery("");
    setCampusFilter("");
    setStandardFilter("");
    setSectionFilter("");
  };

  const handlePageChange = (newPage) => {
    if (newPage !== pageNumber) {
      dispatch(setPageNumber(newPage));
    }
  };

  const toggleView = (index) => {
    const student = currentStudents[index];
    const studentId = student.student?.id;
    setExpandedRowIndex(expandedRowIndex === index ? null : index);
    
    if (expandedRowIndex !== index && studentId) {
      dispatch(fetchIncidentReports(studentId));
    }
  };

  const toggleStudentSelection = (studentId, studentName) => {
    setSelectedStudents((prev) => {
      const isSelected = prev.some((student) => student.id === studentId);
      if (isSelected) {
        return prev.filter((student) => student.id !== studentId);
      } else {
        return [...prev, { id: studentId, name: studentName }];
      }
    });
  };

  const toggleSelectAll = () => {
    if (selectedStudents.length === students.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(
        students.map((student) => ({
          id: student.student?.id,
          name: `${student.student?.firstName} ${student.student?.lastName}`,
        }))
      );
    }
  };

  const onSendNotification = async (payload) => {
    const requestData = {
      UserIds: payload.recipients.map((recipient) => recipient.id),
      Title: payload.title,
      Body: payload.message,
    };

    dispatch(sendNotification(requestData));
  };

  const handleSubmitNotification = (notificationData) => {
    onSendNotification(notificationData);
    setIsNotificationsModal(false);
    setSelectedStudents([]);
  };

  const uniqueCampuses = campusClassesDetail
    ? Array.from(new Set(campusClassesDetail.map((campus) => campus.name)))
    : [];

  const uniqueStandards = reduxCampusId
    ? (() => {
        const selectedCampus = campusClassesDetail.find(
          (campus) => campus.id === reduxCampusId
        );
        return selectedCampus
          ? Array.from(new Set(selectedCampus.classes.map((cls) => cls.className)))
          : [];
      })()
    : [];

  const uniqueSections = reduxClassId
    ? (() => {
        let sections = [];
        campusClassesDetail?.forEach((campus) => {
          const selectedClass = campus.classes.find((cls) => cls.id === reduxClassId);
          if (selectedClass) {
            sections = selectedClass.sections.map((section) => section.sectionName);
          }
        });
        return Array.from(new Set(sections));
      })()
    : [];

  const totalPages = Math.ceil(totalCount / pageSize);
  const currentStudents = students.filter((student) => {
    let matchesTile = true;
    if (tileFilter === "TP") {
      matchesTile = topPerformers.includes(
        student?.academicDetails?.overallAnnualResult
      );
    } else if (tileFilter === "EA") {
      matchesTile = parseFloat(student?.academicDetails?.attendance) >= 90;
    } else if (tileFilter === "FD") {
      matchesTile = student.feeAndDues?.monthlyFee > 0;
    }
    return matchesTile;
  });

  return (
    <>
      <Toaster />
      <div className="container mx-auto p-4 border border-[#241763]">
      {isIncidentReportModalOpen && selectedStudentForIncident && (
          <IncidentReportingModal
            isOpen={isIncidentReportModalOpen}
            onClose={() => {
              setIsIncidentReportModalOpen(false);
              setIncidentToEdit(null);
            }}
            onSubmit={handleIncidentSubmit}
            studentId={selectedStudentForIncident.id}
            studentName={selectedStudentForIncident.name}
            incidentToEdit={incidentToEdit}
          />
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-4">
          {studentcardData.map((card, index) => (
            <div
              key={index}
              onClick={() => handleStudentCardClick(card, index)}
              className="cursor-pointer"
            >
              <StudentCards
                icon={card.icon}
                title={card.title}
                number={card.number}
                isSelected={selectedCard === index}
                className="truncate cursor-pointer"
              />
            </div>
          ))}
        </div>

        <div className="mb-4 flex gap-4">
          <div className="flex flex-grow items-end lg:col-span-2">
            <input
              type="text"
              placeholder={t("SearchByNameOrId")}
              value={searchQuery}
              onChange={handleSearchChange}
              className="w-full p-2 border rounded"
            />
          </div>

          <div>
            <CustomDropdown
              options={uniqueCampuses}
              value={campusFilter}
              onChange={handleCampusChange}
              placeholder={t("Campus")}
            />
          </div>

          <div>
            <CustomDropdown
              options={uniqueStandards}
              value={standardFilter}
              onChange={handleClassChange}
              placeholder={t("AllClasses")}
              disabled={!reduxCampusId}
            />
          </div>

          <div>
            <CustomDropdown
              options={uniqueSections}
              value={sectionFilter}
              onChange={handleSectionChange}
              placeholder={t("AllSections")}
              disabled={!reduxClassId}
            />
          </div>

          <div
            className="p-2 bg-gray-200 rounded flex items-center gap-2 cursor-pointer hover:bg-gray-300"
            onClick={handleClearFilters}
          >
            <FilterX size={18} />
            <span>{t("ClearFilters")}</span>
          </div>

          <div className="sm:flex-[1_1_0%]">
            <select
              className="w-full bg-[#241763] text-white p-2 rounded"
              onChange={(e) => handleUploadTypeChange(e.target.value)}
              value={uploadType}
            >
              <option value="" disabled>
                {t("AddStudent")}
              </option>
              <option value="single">{t("AddStudent")}</option>
              <option value="bulk">{t("BulkUploadStudent")}</option>
            </select>
          </div>
          <div
            className="bg-[#241763] p-2 rounded"
            onClick={() => setIsNotificationsModal(true)}
          >
            <SendHorizonal className="text-white w-6 h-6 group-hover:text-white" />
          </div>
        </div>

        <StudentModal
          isModalOpen={isModalOpen}
          isEdit={isEdit}
          onSubmit={onSubmit}
          register={register}
          handleSubmit={handleSubmit}
          setIsModalOpen={setIsModalOpen}
          password={password}
          reset={reset}
          currentPicture={currentPicture}
          classes={classes}
          sections={sections}
          setSections={setSections}
          uploadType={uploadType}
          onModalClose={handleModalClose}
          selectedClass={selectedClass}
          setSelectedClass={setSelectedClass}
          campuses={campusClassesDetail}
          setValue={setValue}
          getValues={getValues}
          fetchFeeStructures={() => fetchFeeStructures()}
          feeStructures={feeStructures}
        />
        {isIncidentModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[80vh] overflow-y-auto">
              <h2 className="text-xl font-bold mb-4">{t("IncidentDetails")}</h2>
              {selectedIncident && (
                <table className="w-full">
                  <tbody>
                    <tr>
                      <td className="font-semibold py-1">Incident Date</td>
                      <td className="py-1">{selectedIncident.dateTime ? new Date(selectedIncident.dateTime).toLocaleDateString() : 'N/A'}</td>
                    </tr>
                    <tr>
                      <td className="font-semibold py-1">Incident Type</td>
                      <td className="py-1">{selectedIncident.category}</td>
                    </tr>
                    <tr>
                      <td className="font-semibold py-1">Reported By</td>
                      <td className="py-1">{selectedIncident.reporterName}</td>
                    </tr>
                    <tr>
                      <td className="font-semibold py-1">Location</td>
                      <td className="py-1">{selectedIncident.location}</td>
                    </tr>
                    <tr>
                      <td className="font-semibold py-1">Severity</td>
                      <td className="py-1">{selectedIncident.severityLevel}</td>
                    </tr>
                    <tr>
                      <td className="font-semibold py-1">Status</td>
                      <td className="py-1">{selectedIncident.status}</td>
                    </tr>
                    <tr>
                      <td className="font-semibold py-1">Action Taken</td>
                      <td className="py-1">{selectedIncident.actionTaken}</td>
                    </tr>
                    {selectedIncident.involvedParties && (
                      <tr>
                        <td className="font-semibold py-1">Involved Parties</td>
                        <td className="py-1">{selectedIncident.involvedParties}</td>
                      </tr>
                    )}
                    {selectedIncident.assignedTo && (
                      <tr>
                        <td className="font-semibold py-1">Assigned To</td>
                        <td className="py-1">{selectedIncident.assignedTo}</td>
                      </tr>
                    )}
                    <tr>
                      <td className="font-semibold py-1">Description</td>
                      <td className="py-1">{selectedIncident.description}</td>
                    </tr>
                    <tr>
                      <td className="font-semibold py-1">Comments</td>
                      <td className="py-1">{selectedIncident.comments}</td>
                    </tr>
                  </tbody>
                </table>
              )}
              <button
                onClick={() => setIsIncidentModalOpen(false)}
                className="mt-4 bg-[#241763] text-white px-4 py-2 rounded hover:bg-[#241763]/80"
              >
                Close
              </button>
            </div>
          </div>
        )}

        {isNotificationsModal && (
          <NotificationsModal
            selectedItems={selectedStudents}
            toggleItemSelection={toggleStudentSelection}
            handleSubmitNotification={handleSubmitNotification}
            onClose={() => setIsNotificationsModal(false)}
          />
        )}

        {isIncidentModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[80vh] overflow-y-auto">
              <h2 className="text-xl font-bold mb-4">{t("IncidentDetails")}</h2>
              {selectedIncident && (
                <table className="w-full">
                  <tbody>
                    <tr>
                      <td className="font-semibold py-1">Incident Date</td>
                      <td className="py-1">{selectedIncident.date}</td>
                    </tr>
                    <tr>
                      <td className="font-semibold py-1">Incident Type</td>
                      <td className="py-1">{selectedIncident.type}</td>
                    </tr>
                    <tr>
                      <td className="font-semibold py-1">Reported By</td>
                      <td className="py-1">{selectedIncident.reportedBy}</td>
                    </tr>
                    <tr>
                      <td className="font-semibold py-1">Location</td>
                      <td className="py-1">{selectedIncident.location}</td>
                    </tr>
                    <tr>
                      <td className="font-semibold py-1">Severity</td>
                      <td className="py-1">{selectedIncident.severity}</td>
                    </tr>
                    <tr>
                      <td className="font-semibold py-1">Status</td>
                      <td className="py-1">{selectedIncident.status}</td>
                    </tr>
                    <tr>
                      <td className="font-semibold py-1">Action Taken</td>
                      <td className="py-1">{selectedIncident.action}</td>
                    </tr>
                    {selectedIncident.involvedParties && (
                      <tr>
                        <td className="font-semibold py-1">Involved Parties</td>
                        <td className="py-1">
                          {selectedIncident.involvedParties}
                        </td>
                      </tr>
                    )}
                    {selectedIncident.assignedTo && (
                      <tr>
                        <td className="font-semibold py-1">Assigned To</td>
                        <td className="py-1">{selectedIncident.assignedTo}</td>
                      </tr>
                    )}
                    <tr>
                      <td className="font-semibold py-1">Comments</td>
                      <td className="py-1">{selectedIncident.comments}</td>
                    </tr>
                  </tbody>
                </table>
              )}
              <button
                onClick={() => setIsIncidentModalOpen(false)}
                className="mt-4 bg-[#241763] text-white px-4 py-2 rounded hover:bg-[#241763]/80"
              >
                Close
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center h-40">
            <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full text-[#241763]"></div>
            <span className="ml-2 text-[#241763]">Loading...</span>
          </div>
        ) : (
          <div className="max-h-[70vh] overflow-x-auto overflow-y-auto">
            <table className="table-auto w-full mt-6">
              <thead className="py-4 border-b font-bold">
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={
                        selectedStudents.length > 0 &&
                        selectedStudents.length === students.length
                      }
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th>Profile</th>
                  <th>Name</th>
                  <th>Gender</th>
                  <th>Email</th>
                  <th>Admission Date</th>
                  <th>Phone</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody className="text-sm">
              {currentStudents?.map((student, index) => {
                 const incidentsArray = Array.isArray(incidentReports) ? incidentReports : [];
                 console.log('incidentReports:', incidentReports);
                 const studentIncidents = incidentsArray.filter(
                  (incident) => incident.userId === student.student?.id
                );
                  return (
                    <>
                      <tr
                        key={student.student?.id}
                        className="p-2 py-2 hover:bg-gray-200 space-y-2"
                      >
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedStudents.some(
                              (s) => s.id === student.student?.id
                            )}
                            onChange={() =>
                              toggleStudentSelection(
                                student.student?.id,
                                `${student.student?.firstName} ${student.student?.lastName}`
                              )
                            }
                          />
                        </td>
                        <td>
                          {student.student?.profilePicture ? (
                            <img
                              src={student.student.profilePicture}
                              alt="Student Profile"
                              className="w-8 h-8 sm:w-10 sm:h-10 rounded-full"
                            />
                          ) : (
                            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gray-200 flex items-center justify-center">
                              <User className="w-6 h-6 text-gray-500" />
                            </div>
                          )}
                        </td>
                        <td>{`${student.student?.firstName} ${student.student?.lastName}`}</td>
                        <td>{student.student?.gender}</td>
                        <td>{student.student?.email}</td>
                        <td>{student.student?.admissionDate}</td>
                        <td className="text-blue-500">{student.student?.phone}</td>
                        <td className="space-x-2 flex items-center py-2">
                          <button
                            className="bg-[#B6A5C9]/50 text-white p-2 rounded mr-2 hover:bg-[#241763]/60 group"
                            onClick={() => handleEdit(student)}
                          >
                            <Pencil className="text-[#241763] w-4 h-4 group-hover:text-white" />
                          </button>
                          <button
                            className="bg-[#B6A5C9]/50 text-white p-2 rounded hover:bg-[#241763]/60 group"
                            onClick={() => handleDelete(student.student?.id)}
                          >
                            <Trash2 className="text-[#241763] w-4 h-4 group-hover:text-white" />
                          </button>
                          <div
                            onClick={() => toggleView(index)}
                            className="cursor-pointer hover:bg-[#241763]/60 group bg-[#B6A5C9]/50 p-2 rounded"
                          >
                            {expandedRowIndex === index ? (
                              <ChevronUp size={16} className="group-hover:text-white" />
                            ) : (
                              <ChevronDown size={16} className="group-hover:text-white" />
                            )}
                          </div>
                        </td>
                      </tr>
                      {expandedRowIndex === index && (
                        <tr>
                          <td colSpan="10">
                            <div className="bg-gray-100 p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                              <div className="bg-white shadow-md rounded-lg p-4">
                                <h1 className="font-bold text-base text-blue-400 mb-2">
                                  Personal Details
                                </h1>
                                <table className="w-full">
                                  <tbody>
                                    <tr>
                                      <td className="font-semibold">Location</td>
                                      <td>{student.student?.location}</td>
                                    </tr>
                                    <tr>
                                      <td className="font-semibold">Enroll</td>
                                      <td>{student.student?.enrollmentNo}</td>
                                    </tr>
                                    <tr>
                                      <td className="font-semibold">DOB</td>
                                      <td>{student.student?.dob}</td>
                                    </tr>
                                    <tr>
                                      <td className="font-semibold">DOK Level</td>
                                      <td>{student.student?.dokLevel || "Not specified"}</td>
                                    </tr>
                                    <tr>
                                      <td className="font-semibold">Marital Status</td>
                                      <td>{student.student?.maritalStatus}</td>
                                    </tr>
                                    <tr>
                                      <td className="font-semibold">Stud Status</td>
                                      <td>{student.student?.userStatus}</td>
                                    </tr>
                                    <tr>
                                      <td className="font-semibold">isActive</td>
                                      <td>{student.student?.isActive ? "Yes" : "No"}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>

                              <div className="bg-white shadow-md rounded-lg p-4">
                                <h1 className="font-bold text-base text-blue-400 mb-2">
                                  Parent Details
                                </h1>
                                <table className="w-full">
                                  <tbody>
                                    <tr>
                                      <td className="font-semibold">Father Name</td>
                                      <td>{student.userDetails?.fatherName}</td>
                                    </tr>
                                    <tr>
                                      <td className="font-semibold">CNIC</td>
                                      <td>{student.userDetails?.fatherCNIC}</td>
                                    </tr>
                                    <tr>
                                      <td className="font-semibold">Contact</td>
                                      <td>{student.userDetails?.fatherContact}</td>
                                    </tr>
                                    <tr>
                                      <td className="font-semibold">Profession</td>
                                      <td>{student.userDetails?.fatherProfession}</td>
                                    </tr>
                                    <tr>
                                      <td className="font-semibold">Mother Name</td>
                                      <td>{student.userDetails?.motherName}</td>
                                    </tr>
                                    <tr>
                                      <td className="font-semibold">CNIC</td>
                                      <td>{student.userDetails?.motherCNIC}</td>
                                    </tr>
                                    <tr>
                                      <td className="font-semibold">Contact</td>
                                      <td>{student.userDetails?.motherContact}</td>
                                    </tr>
                                    <tr>
                                      <td className="font-semibold">Profession</td>
                                      <td>{student.userDetails?.motherProfession}</td>
                                    </tr>
                                    <tr>
                                      <td className="font-semibold">Blood</td>
                                      <td>{student.userDetails?.bloodGroup}</td>
                                    </tr>
                                    <tr>
                                      <td className="font-semibold">Parent Email</td>
                                      <td>{student.userDetails?.parentEmail}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>

                              <div className="bg-white shadow-md rounded-lg p-4">
                                <h1 className="font-bold text-base text-blue-400 mb-2">
                                  Assigned Classes
                                </h1>
                                <table className="w-full border-collapse border border-gray-300">
                                  <thead>
                                    <tr>
                                      <th className="border border-gray-300 px-2 py-1">Class</th>
                                      <th className="border border-gray-300 px-2 py-1">Section</th>
                                      <th className="border border-gray-300 px-2 py-1">Subjects</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {student.assignedClasses?.map(
                                      (assignedClass, classIndex) =>
                                        assignedClass.classSections?.map(
                                          (section, sectionIndex) => (
                                            <tr key={`${classIndex}-${sectionIndex}`}>
                                              <td className="border border-gray-300 px-2 py-1">
                                                {assignedClass.className || "N/A"}
                                              </td>
                                              <td className="border border-gray-300 px-2 py-1">
                                                {section.sectionName || "N/A"}
                                              </td>
                                              <td className="border border-gray-300 px-2 py-1">
                                                {section.subjects?.filter(Boolean).join(", ") || "N/A"}
                                              </td>
                                            </tr>
                                          )
                                        )
                                    )}
                                  </tbody>
                                </table>
                              </div>

                              <div className="bg-white shadow-md rounded-lg p-4">
                                <h1 className="font-bold text-base text-blue-400 mb-2">
                                  Academic Performance
                                </h1>
                                <table className="w-full">
                                  <tbody>
                                    <tr>
                                      <td className="font-semibold">Classes</td>
                                      <td>{student.academicDetails?.class}</td>
                                    </tr>
                                    <tr>
                                      <td className="font-semibold">Course Level</td>
                                      <td>{student.academicDetails?.courseLevel}</td>
                                    </tr>
                                    <tr>
                                      <td className="font-semibold">Section</td>
                                      <td>{student.academicDetails?.section}</td>
                                    </tr>
                                    <tr>
                                      <td className="font-semibold">1st Term Result</td>
                                      <td>{student.academicDetails?.firstSemResult}</td>
                                    </tr>
                                    <tr>
                                      <td className ="font-semibold">2nd Term Result</td>
                                      <td>{student.academicDetails?.secondSemResult}</td>
                                    </tr>
                                    <tr>
                                      <td className="font-semibold">3rd Term Result</td>
                                      <td>{student.academicDetails?.thirdSemResult}</td>
                                    </tr>
                                    <tr>
                                      <td className="font-semibold">Overall Annual Result</td>
                                      <td>{student.academicDetails?.overallAnnualResult}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>

                              <div className="bg-white shadow-md rounded-lg p-4">
                                <h1 className="font-semibold text-base text-blue-400 mb-2 mt-2">
                                  Fee & Dues Details
                                </h1>
                                <table className="w-full">
                                  <tbody>
                                    <tr>
                                      <td className="font-semibold">Monthly Fee</td>
                                      <td>{student.feeAndDues?.monthlyFee}</td>
                                    </tr>
                                    <tr>
                                      <td className="font-semibold">Any Special Discount</td>
                                      <td>{student.feeAndDues?.anySpecialDiscount}</td>
                                    </tr>
                                    <tr>
                                      <td className="font-semibold">Sibling Discount</td>
                                      <td>{student.feeAndDues?.siblingDiscount}</td>
                                    </tr>
                                    <tr>
                                      <td className="font-semibold">Employee Kids Discount</td>
                                      <td>{student.feeAndDues?.employeeKidDiscount}</td>
                                    </tr>
                                    <tr>
                                      <td className="font-semibold">LMS Fee</td>
                                      <td>{student.feeAndDues?.lmsFee}</td>
                                    </tr>
                                    <tr>
                                      <td className="font-semibold">Extra Curricular Fee</td>
                                      <td>{student.feeAndDues?.extraCurricularActFee}</td>
                                    </tr>
                                    <tr>
                                      <td className="font-semibold">total amount</td>
                                      <td>{student.feeAndDues?.totalAmount}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>

                              <div className="bg-white shadow-md rounded-lg p-4">
                                <div className="flex justify-between items-center mb-2">
                                  <h1 className="font-bold text-base text-blue-400">
                                    Incident Reporting ({studentIncidents.length})
                                  </h1>
                                  <button
                                    onClick={() => openIncidentReportModal(student)}
                                    className="bg-[#241763] text-white p-1.5 rounded-full hover:bg-[#241763]/80 flex items-center justify-center"
                                    title="Add Incident Report"
                                  >
                                    <Plus size={16} />
                                  </button>
                                </div>
                                {studentIncidents.length > 0 ? (
                                  <div className="space-y-2 max-h-48 overflow-y-auto">
                                    {studentIncidents.map((incident, index) => (
                                      <div key={incident.id} className="border-b pb-2 flex justify-between items-center">
                                        <button
                                          onClick={() => {
                                            setSelectedIncident(incident);
                                            setIsIncidentModalOpen(true);
                                          }}
                                          className="text-blue-500 hover:text-blue-700 underline text-left flex-1"
                                        >
                                          <div className="flex justify-between items-center">
                                            <div className="flex items-center">
                                              <AlertCircle
                                                size={16}
                                                className={`mr-1 ${
                                                  incident.severityLevel === "Critical"
                                                    ? "text-red-500"
                                                    : incident.severityLevel === "High"
                                                    ? "text-orange-500"
                                                    : incident.severityLevel === "Medium"
                                                    ? "text-yellow-500"
                                                    : "text-green-500"
                                                }`}
                                              />
                                              <span>{incident.category}</span>
                                            </div>
                                            <span className="text-sm text-gray-500">
                                              {incident.dateTime ? new Date(incident.dateTime).toLocaleDateString() : 'N/A'}
                                            </span>
                                          </div>
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="flex flex-col items-center justify-center py-4 text-gray-500">
                                    <p>No incidents reported</p>
                                    <p className="text-sm">Click the + button to add a report</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex justify-between items-center mt-4">
          <button
            onClick={() => handlePageChange(pageNumber - 1)}
            className="px-3 py-1 bg-[#241763] text-white rounded"
            disabled={pageNumber === 1}
          >
            Previous
          </button>

          <div className="flex">
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => handlePageChange(i + 1)}
                className={`px-3 py-1 mx-1 border rounded ${
                  pageNumber === i + 1
                    ? "bg-[#241763] text-white"
                    : "bg-gray-200"
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>

          <button
            onClick={() => handlePageChange(pageNumber + 1)}
            className="px-3 py-1 bg-[#241763] text-white rounded"
            disabled={pageNumber === totalPages}
          >
            Next
          </button>
        </div>
      </div>
    </>
  );
};

export default Students;